import { Component, EventEmitter, Input, OnInit, Output, AfterViewInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, finalize, of, Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { Business } from '../../../models/domain/Business';
import { BusinessService } from '../../../services/business.service';
import { LoadingService } from '../../../services/loading.service';
import { ObjectUtility } from '../../../utilities/ObjectUtility';
import { whitespaceValidator } from '../../../validators/ValidationService';

@Component({
  selector: 'app-business-details',
  templateUrl: './business-details.component.html',
  styleUrl: './business-details.component.scss',
  imports: [ReactiveFormsModule, CommonModule],
})

export class BusinessDetailsComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() classApplied = new EventEmitter<boolean>();
  @Output() saveConfirmationEvent = new EventEmitter<boolean>();
  @Input() data!: Business;
  // isToggled
  isToggled = false;
  frmGroup!: FormGroup;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  public businessOriginal = new Business;
  public business: Business = new Business;
  public titleText = "";
  public titleButton = "";
  public errorMessage = "";
  public currentBusinessId = 0;
  isSubmitted = false;

  constructor(
    public themeService: CustomizerSettingsService,
    private formBuilder: FormBuilder,
    private businessService: BusinessService,
    private toasterService: ToastrService,
    private loadingService: LoadingService
  ) {
    this.initialize();
  }

  public ngOnInit(): void {
    if (this.data) {
      this.currentBusinessId = this.data.id ? this.data.id : 0;
      this.business = ObjectUtility.deepClone(this.data);
      this.businessOriginal = ObjectUtility.deepClone(this.data);
      this.setForm(this.business);
    }
    else {
      this.currentBusinessId = 0;
      const newBusiness = new Business();
      this.business = ObjectUtility.deepClone(newBusiness);
      this.businessOriginal = ObjectUtility.deepClone(newBusiness);
      this.setForm(this.business);
    }
  }

  private initialize(): void {
    this.themeService.isToggled$.subscribe(isToggled => {
      this.isToggled = isToggled;
    });
    this.isSubmitted = false;
    this.createForm();
  }

  public ngAfterViewInit(): void {

    this.frmGroup.controls['nameControl'].valueChanges.subscribe(value => {
      this.business.name = value;
    });

    this.frmGroup.controls['registrationNoControl'].valueChanges.subscribe(value => {
      this.business.registrationNo = value;
    });

    this.frmGroup.controls['incorporationDateControl'].valueChanges.subscribe(value => {
      this.business.incorporationDate = value;
    });

  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get f() { return this.frmGroup.controls; }

  private createForm(): void {
    this.frmGroup = this.formBuilder.group({
      nameControl: new FormControl('', [Validators.required, whitespaceValidator()]),
      registrationNoControl: new FormControl('', [whitespaceValidator()]),
      incorporationDateControl: new FormControl('', [whitespaceValidator()])
    })
  }

  private setForm(business: Business): void {
    if (business) {

      let date = null;
      let formattedDate = null;
      if (business.id != 0) {
        date = new Date(business?.incorporationDate);  // Create a Date object
        formattedDate = date.toISOString().split('T')[0];
      }

      this.frmGroup.patchValue({
        nameControl: business.name,
        registrationNoControl: business.registrationNo,
        incorporationDateControl: formattedDate,

      });
    }
    this.titleText = `${this.currentBusinessId === 0 ? "Add" : "Edit"} Business`;
    this.titleButton = `${this.currentBusinessId === 0 ? "Create" : "Update"}`;
  }

  public onSave(): void {
    this.isSubmitted = true;
    if (this.frmGroup.valid) {
      this.loadingService.showLoading();
      this.addEditBusiness();
    }
  }

  private addEditBusiness(): void {
    if (this.business?.id === 0) {
      this.businessService.create(this.business)
        .pipe(
          catchError((err: any) => {
            if (err && err.error) {
              this.isSubmitted = false;
              if (err.error.isCustom) {
                this.errorMessage = err.error.message;
                this.toasterService.warning(err.error.message);
              } else {
                this.toasterService.error('Something went wrong!');
              }
            } else {
              this.isSubmitted = false;
              this.toasterService.error('Something went wrong!');
            }
            this.loadingService.hideLoading();

            return of(null);
          }),
          finalize(() => {
            this.loadingService.hideLoading();
          }),
          takeUntil(this.destroy$))
        .subscribe(response => {
          if (response) {
            this.isSubmitted = false;
            this.business = response?.body != null ? response?.body : new Business;
            this.saveConfirmationEvent.emit(true);
            this.toggleClass();
            this.toasterService.success("Save successfully");
          }
          this.loadingService.hideLoading();
        });
    } else {
      this.businessService.update(this.business)
        .pipe(
          catchError((err: any) => {
            if (err && err.error) {
              this.isSubmitted = false;
              if (err.error.isCustom) {
                this.errorMessage = err.error.message;
                this.toasterService.warning(err.error.message);
              } else {
                this.toasterService.error('Something went wrong!');
              }
            } else {
              this.isSubmitted = false;
              this.toasterService.error('Something went wrong!');
            }
            return of(null);
          }),
          takeUntil(this.destroy$),
          // Ensure loading is always hidden, even if error is not caught
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(response => {
          if (response) {
            this.isSubmitted = false;
            this.business = response?.body != null ? response?.body : new Business;
            this.saveConfirmationEvent.emit(true);
            this.toasterService.success("Update successfully");
            this.toggleClass();
          }
        });
    }
  }


  public toggleClass(): void {
    this.classApplied.emit(false);
  }

  public dataHasChanged(): boolean {
    const baseEqual = ObjectUtility.deepEqual(this.business, this.businessOriginal);
    return !baseEqual;
  }

  public formIsValid(): boolean {
    const isValid = this.frmGroup.controls['nameControl'].valid
    return isValid;
  }


}